import { HttpErrorResponse } from '@angular/common/http';
import { EventEmitter, Injectable, OnDestroy, Output } from '@angular/core';
import { Admin, UserSetting } from '@wb/core/interfaces/admin';
import { AdminGQL, AdminResponse } from '@wb/core/schema/query-definitions/admin.graphql';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';
import { UpdateUserSettingsGQL } from '../schema/mutation-definitions/users_admin.graphql';
import { UserSettingsGQL } from '../schema/query-definitions/users_admin.graphql';

@Injectable({
  providedIn: 'root',
})
export class UserService implements OnDestroy {
  @Output() emitLoadingEvent = new EventEmitter<boolean>();

  private userDetails$ = new Subject<Admin | null>();

  private userUid$ = new BehaviorSubject<string>('');

  private destroyed$ = new Subject<void>();

  userSettings: UserSetting[] = [];

  userFullName?: string;

  userPin?: string;

  fetchingPin = false;

  private isKioskModeEnabled: boolean = false;

  updateUserSettingsDone$: BehaviorSubject<Promise<boolean>[] | undefined> = new BehaviorSubject<any>(undefined);

  constructor(
    private readonly adminGQL: AdminGQL,
    private readonly userSettingsGQL: UserSettingsGQL,
    private updateUserSettingsGQL: UpdateUserSettingsGQL,
  ) {}

  get getKioskModeEnabled() {
    return this.isKioskModeEnabled || localStorage.getItem('kioskModeEnabled') === 'true';
  }

  set setKioskModeEnabled(enabled: boolean) {
    localStorage.setItem('kioskModeEnabled', `${enabled}`);
    this.isKioskModeEnabled = enabled;
  }

  createUserUidSubscription() {
    this.userUid$
      .asObservable()
      .pipe(takeUntil(this.destroyed$))
      .subscribe((userUid) => {
        if (!userUid) {
          return;
        }

        this.getUserDetails(userUid);
      });
  }

  ngOnDestroy() {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  getUserDetails(uid: string) {
    this.adminGQL
      .fetch({ uid }, { fetchPolicy: 'network-only' })
      .pipe(
        map((result) => result.data),
        takeUntil(this.destroyed$),
      )
      .subscribe({
        next: (result: AdminResponse) => {
          if (!result || !result.admin || !result.admin.success) {
            return;
          }
          const admin = result.admin.admin;
          this.userDetails$.next(admin);
          this.userFullName = `${admin.first_name} ${admin.last_name}`;
          this.userSettings = admin?.user_settings;
          this.setUserPin();
        },
        error: (err: HttpErrorResponse) => {
          if (err) {
            console.error(err);
          }
        },
      });
  }

  setUserUid(uid: string) {
    this.userUid$.next(uid);
  }

  get userDetails() {
    return this.userDetails$.asObservable();
  }

  get getUserFullName() {
    return this.userFullName;
  }

  async getUserPin(userUid?: string) {
    await this.fetchPin(userUid);
    return this.userPin;
  }

  get updateUserSettingsDone(): Observable<Promise<boolean>[] | undefined> {
    return this.updateUserSettingsDone$.asObservable();
  }

  private fetchPin(userUid?: string) {
    this.fetchingPin = true;
    this.userSettingsGQL
      .fetch({ uid: userUid }, { fetchPolicy: 'network-only' })
      .pipe(
        map((response) => response),
        takeUntil(this.destroyed$),
      )
      .subscribe({
        next: (response: any) => {
          this.userSettings = response.data.userSettings.setting.map((e: any) => {
            return {
              key: e.key,
              value: e.value,
            };
          });
          this.setUserPin();
          this.fetchingPin = false;
        },
      });
  }

  private setUserPin() {
    try {
      this.userPin = this.userSettings[Object.values(this.userSettings).findIndex((item) => item.key === 'pin')].value;
    } catch {
      this.userPin = undefined;
    }
  }

  updatePin(userUid?: string, userPin?: string): Promise<void> {
    let sendUpdatePin = undefined;
    return new Promise((resolveReturn) => {
      sendUpdatePin = new Promise<boolean>((resolve) => {
        this.updateUserSettingsGQL
          .mutate({ uid: userUid, input: [{ key: 'pin', value: userPin }] }, { fetchPolicy: 'network-only' })
          .pipe(
            map((result) => result.data),
            takeUntil(this.destroyed$),
          )
          .subscribe({
            next: (response: any) => {
              this.emitLoadingEvent.emit(false);
              resolveReturn();
              resolve(response.updateUserSettings.success);
            },
            error: (error) => {
              this.emitLoadingEvent.emit(false);
              console.error(error);
            },
          });
      });
      this.updateUserSettingsDone$.next([sendUpdatePin]);
    });
  }
}
